import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Badge } from '@/backoffice/components/ui';
import { fetchRelayPointDashboard, type RelayPointDashboard } from '@/domains/api/relay-point.api';
import { formatRelativeTime } from '@/backoffice/utils';
import { Building2, Loader2, Package, Activity } from 'lucide-react';

export function RelayPointDashboardPage() {
  const [data, setData] = useState<RelayPointDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRelayPointDashboard()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout userType="relay_point" title="Tableau de bord">
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout userType="relay_point" title="Tableau de bord">
        <Card><p className="text-center text-gray-500 py-8">Impossible de charger le point relais.</p></Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="relay_point" title="Tableau de bord">
      <Card className="mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="w-8 h-8 text-teal-600" />
          <div>
            <h2 className="text-xl font-semibold">{data.relay_point.name}</h2>
            <p className="text-sm text-gray-600">{data.relay_point.address}</p>
            {data.relay_point.opening_hours && (
              <p className="text-xs text-gray-500 mt-1">Horaires : {data.relay_point.opening_hours}</p>
            )}
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Card>
          <p className="text-sm text-gray-500">Colis en attente</p>
          <p className="text-3xl font-bold text-amber-600">{data.stats.packages_waiting}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">En stockage</p>
          <p className="text-3xl font-bold text-blue-600">{data.stats.packages_in_store}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Activités aujourd&apos;hui</p>
          <p className="text-3xl font-bold text-teal-600">{data.stats.activities_today}</p>
        </Card>
      </div>

      <Card>
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-teal-600" /> Activité récente
        </h3>
        {data.recent_activities.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune activité récente.</p>
        ) : (
          <div className="space-y-2">
            {data.recent_activities.map((ev) => (
              <div key={ev.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                <div className="flex justify-between">
                  <Badge variant="default">{ev.event_type}</Badge>
                  <span className="text-xs text-gray-400">{ev.created_at ? formatRelativeTime(ev.created_at) : ''}</span>
                </div>
                {ev.notes && <p className="text-gray-600 mt-1">{ev.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </Card>
    </DashboardLayout>
  );
}
